import { useEffect, useRef, useState } from 'react'

/*
  A meeting as it happens.

  The API has streamed this since §7.2 was built and nothing in the product ever opened the
  socket, so a meeting that takes a minute to run looked instantaneous and empty: you left
  the page with nothing next and came back to a finished document. Watching the board work
  is most of what makes it feel like a board.

  Two decisions worth keeping.

  The stage list keys off phase, not round. Phases are a closed set in ProgressPhase;
  rounds are not, and round 5 is the pushback path rather than a fifth round of argument.
  Hardcoding round numbers here would mean this screen drifts every time the orchestrator
  changes shape, so every round event stacks under one stage instead.

  Nothing here is gold. In this screen gold means nothing, and a stage that is running must
  not be confusable with a branded surface, so live is navy and only failure is red.
*/

const PHASES = ['prewarm', 'round', 'synthesis', 'commitments', 'complete', 'failed'] as const

interface Progress {
  round: number
  phase: string
  detail: string | null
  at: string
}

/**
 * The four stages an owner can see happening. Complete and failed are endings rather than
 * stages, so they are not in here: they change what the whole screen says.
 */
const STAGES: { phase: string; label: string; idle: string }[] = [
  { phase: 'prewarm',     label: 'Preparing the room',    idle: 'Everything the board already knows about you, loaded once.' },
  { phase: 'round',       label: 'The board argues',      idle: 'Each seat takes a position, then answers the others.' },
  { phase: 'synthesis',   label: 'The chair synthesizes', idle: 'One decision out of five arguments.' },
  { phase: 'commitments', label: 'Commitments recorded',  idle: 'What was decided becomes work with a date on it.' },
]

export function LiveMeeting({
  meetingId, onBack, onOpenMinutes,
}: {
  meetingId: string
  onBack: () => void
  onOpenMinutes: () => void
}) {
  const [events, setEvents] = useState<Progress[]>([])
  const [link, setLink] = useState<'opening' | 'live' | 'dropped'>('opening')
  const [elapsed, setElapsed] = useState(0)
  const started = useRef<number>(Date.now())

  useEffect(() => {
    const source = new EventSource(`/api/meetings/${meetingId}/progress`)

    const take = (raw: MessageEvent) => {
      const e = JSON.parse(raw.data) as { Round: number; Phase: string; Detail: string | null; CreatedAt: string }
      setLink('live')
      setEvents(prior => [...prior, { round: e.Round, phase: e.Phase, detail: e.Detail, at: e.CreatedAt }])

      // The server closes after either ending. Closing this end too stops EventSource
      // reconnecting for a meeting that has nothing left to say.
      if (e.Phase === 'complete' || e.Phase === 'failed') source.close()
    }

    // Named events, so onmessage never fires. Every phase has to be subscribed by name.
    for (const phase of PHASES) source.addEventListener(phase, take as EventListener)

    // EventSource reconnects on its own and replays from Last-Event-ID, so a drop is worth
    // saying out loud but is not worth a retry button.
    source.onopen  = () => setLink('live')
    source.onerror = () => setLink(prior => (prior === 'live' ? 'dropped' : prior))

    return () => source.close()
  }, [meetingId])

  const finished = events.find(e => e.phase === 'complete' || e.phase === 'failed')

  useEffect(() => {
    if (finished) return
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - started.current) / 1000)), 1000)
    return () => clearInterval(tick)
  }, [finished])

  const failed = finished?.phase === 'failed'
  const reached = STAGES.map(s => events.filter(e => e.phase === s.phase))
  const furthest = reached.reduce((last, hits, i) => (hits.length > 0 ? i : last), -1)

  return (
    <article className="live">
      <button className="linkish minutes__back" onClick={onBack}>← All meetings</button>

      <header className="live__head">
        <p className="live__eyebrow">
          {finished ? (failed ? 'Meeting stopped' : 'Meeting closed') : 'In session'}
          {!finished && <span className="live__pulse" aria-hidden />}
        </p>
        <h1 className="screen-title">
          {finished
            ? (failed ? 'The board did not finish' : 'The board has decided')
            : 'Your board is meeting'}
        </h1>
        {!finished && (
          <p className="live__elapsed">
            {clock(elapsed)} elapsed
            {link === 'dropped' && <span className="live__dropped"> · reconnecting</span>}
          </p>
        )}
      </header>

      {events.length === 0 && !finished && (
        <p className="live__waiting">Waiting for the board to sit down.</p>
      )}

      <ol className="live__stages" aria-live="polite">
        {STAGES.map((stage, i) => {
          const hits = reached[i]
          const state = finished && !failed ? 'done'
            : i < furthest ? 'done'
            : i === furthest ? (failed ? 'stopped' : 'now')
            : 'waiting'

          return (
            <li key={stage.phase} className={`stage stage--${state}`}>
              <span className="stage__mark" aria-hidden />
              <div className="stage__body">
                <p className="stage__label">{stage.label}</p>

                {hits.length === 0
                  ? <p className="stage__idle">{stage.idle}</p>
                  : hits.map((e, j) => (
                      <p key={j} className="stage__line">
                        {e.detail ?? stage.idle}
                        <span className="stage__at">{time(e.at)}</span>
                      </p>
                    ))}
              </div>
            </li>
          )
        })}
      </ol>

      {finished && !failed && (
        <section className="live__end">
          {finished.detail && <p className="live__recommendation">{finished.detail}</p>}
          <button className="btn btn--brand" onClick={onOpenMinutes}>Read the minutes</button>
        </section>
      )}

      {failed && (
        <section className="live__failed">
          <p className="live__failed-title">The meeting stopped before it finished.</p>
          {finished?.detail && <p className="live__failed-why">{finished.detail}</p>}
          <p className="live__failed-note">
            It is queued to run again on its own. Nothing you put on the table was lost.
          </p>
        </section>
      )}
    </article>
  )
}

/** m:ss. A meeting runs about a minute, so an hour component would be dead weight. */
function clock(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function time(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}
